import Link from 'next/link';
import { FC } from 'react';
import { IoGitNetworkOutline } from 'react-icons/io5';

import { Anchor, Box, Button, ButtonProps, Card, Center, Spoiler, Stack, Text } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';

import { Project, PublishedStatus, SavedItemType } from 'models';
import { ProjectHelpers } from 'helpers/ProjectHelpers';

import { SaveButton } from 'components/buttons/SaveButton';
import { ProfileCard } from 'components/company-profile/components/ProfileCard';
import { DesignLibraryTeaserIndicators } from 'components/design-library-teaser/DesignLibraryTeaser.Indicators';
import { DesignLibraryTeaserTags } from 'components/design-library-teaser/DesignLibraryTeaser.Tags';
import { IKImage } from 'components/ik-image/IKImage';
import { PublishedBadge } from 'components/published-badge/PublishedBadge';

import { DesignLibraryTeaserCreatedBy } from './DesignLibraryTeaserCreatedBy';

import cx from './DesignLibraryTeaser.module.scss';

const DesignLibraryTeaser: FC<{
    project: Project;
    handleTagClick?: (tag: string) => void;
    showPublishedStatus?: boolean;
    isLight?: boolean;
    isExternal?: boolean;
    readMoreButton?: ButtonProps;
    variant?: 'default' | 'profile';
}> = ({ project, handleTagClick, showPublishedStatus, isLight, isExternal, readMoreButton, variant = 'default' }) => {
    const { ref, width } = useElementSize();
    const projectUrl = ProjectHelpers.urls.editor(project.id);

    const thumbnailSize = {
        width: 400,
        height: 200,
    };

    if (width >= 600) {
        thumbnailSize.width = 400;
        thumbnailSize.height = 300;
    }

    if (variant === 'profile') {
        return (
            <ProfileCard
                href={projectUrl}
                target={isExternal ? '_blank' : undefined}
                image={
                    project.thumbnail ? (
                        <IKImage fileOrId={project.thumbnail} width={600} alt={project.name} />
                    ) : (
                        <Text size="sm" c="dimmed">
                            No preview available
                        </Text>
                    )
                }
                actions={<SaveButton iconOnly id={project.id} name={project.name} type={SavedItemType.PROJECT} />}
                eyebrow="Reference design"
                eyebrowIcon={<IoGitNetworkOutline />}
                title={project.name}
                description={project.description}
                cta="Open design"
            />
        );
    }

    return (
        <Box className={cx.container} data-light-teaser={isLight} ref={ref} w="100%" h="100%">
            <Card withBorder className={cx.card}>
                <Box className={cx.inner} h="100%">
                    <Box className={cx.image}>
                        <Link href={projectUrl} target={isExternal ? '_blank' : undefined} className={cx.thumbnail}>
                            {project.thumbnail ? (
                                <IKImage
                                    fileOrId={project.thumbnail}
                                    width={thumbnailSize.width}
                                    height={thumbnailSize.height}
                                    alt={project.name}
                                />
                            ) : (
                                <Center h="100%">
                                    <Text size="sm" c="dimmed">
                                        No preview available
                                    </Text>
                                </Center>
                            )}
                        </Link>
                        {showPublishedStatus && project.visibility !== 'marketplace' && (
                            <Box className={cx.publishedStatus}>
                                <PublishedBadge variant="filled" status={PublishedStatus.DRAFT} />
                            </Box>
                        )}
                        <Box className={cx.saveButton}>
                            <SaveButton iconOnly id={project.id} name={project.name} type={SavedItemType.PROJECT} />
                        </Box>
                    </Box>

                    <Stack className={cx.main}>
                        <Box>
                            <Anchor
                                component={Link}
                                href={projectUrl}
                                className={cx.title}
                                target={isExternal ? '_blank' : undefined}
                            >
                                {project.name}
                                <br />
                            </Anchor>

                            <Box className={cx.author} c="dimmed">
                                <DesignLibraryTeaserCreatedBy project={project} />
                            </Box>
                        </Box>

                        {project.description && (
                            <Box className={cx.description}>
                                <Spoiler
                                    className={cx.spoiler}
                                    maxHeight={65}
                                    showLabel="read more"
                                    hideLabel="read less"
                                >
                                    {project.description}
                                </Spoiler>
                            </Box>
                        )}

                        <DesignLibraryTeaserTags project={project} handleTagClick={handleTagClick} />
                    </Stack>

                    <Stack className={cx.secondary} gap="lg" mt="auto">
                        <DesignLibraryTeaserIndicators project={project} />
                        {/* <DesignLibraryTeaserComponents components={project.template.components as any} /> */}
                    </Stack>

                    {readMoreButton && (
                        <Button
                            component={Link}
                            href={projectUrl}
                            target={isExternal ? '_blank' : undefined}
                            style={{ justifySelf: 'start' }}
                            variant="default"
                            size="xs"
                            {...readMoreButton}
                        />
                    )}
                </Box>
            </Card>
        </Box>
    );
};

export { DesignLibraryTeaser };
